import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, TextField } from '@mui/material';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { debeResolverCodigoArticulo } from '../../utils/chequeTerceroRechazo';
import RecetaArticuloCodigoField from '../../pages/produccion/RecetaArticuloCodigoField';
import RecetaArticuloSearchDialog, {
  RecetaArticuloSeleccionado,
} from '../../pages/produccion/RecetaArticuloSearchDialog';

export interface ArticuloRechazoSeleccionado {
  codigoArticulo: string;
  descripcion: string;
}

interface Props {
  codigoArticulo: string;
  descripcion: string;
  disabled?: boolean;
  onChange: (articulo: ArticuloRechazoSeleccionado) => void;
}

const ChequeTerceroRechazoArticuloField: React.FC<Props> = ({
  codigoArticulo,
  descripcion,
  disabled,
  onChange,
}) => {
  const [codigoInput, setCodigoInput] = useState(codigoArticulo);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applyRequestIdRef = useRef(0);

  useEffect(() => {
    setCodigoInput(codigoArticulo);
  }, [codigoArticulo]);

  const articuloResuelto =
    codigoArticulo.trim().length > 0 &&
    descripcion.trim().length > 0 &&
    codigoInput.trim().toUpperCase() === codigoArticulo.trim().toUpperCase();

  const aplicarProducto = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      const requestId = ++applyRequestIdRef.current;

      if (!trimmed) {
        onChange({ codigoArticulo: '', descripcion: '' });
        setError(null);
        return;
      }

      setResolving(true);
      setError(null);
      try {
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        if (requestId !== applyRequestIdRef.current) return;

        onChange({
          codigoArticulo: producto.codigoArticulo.trim(),
          descripcion: String(producto.descripcion ?? '').trim(),
        });
        setCodigoInput(producto.codigoArticulo.trim());
      } catch {
        if (requestId !== applyRequestIdRef.current) return;
        setError(`No se encontró el artículo ${trimmed}.`);
      } finally {
        if (requestId === applyRequestIdRef.current) setResolving(false);
      }
    },
    [onChange]
  );

  const handleSelect = (item: RecetaArticuloSeleccionado) => {
    applyRequestIdRef.current += 1;
    setError(null);
    setCodigoInput(item.codigoArticulo);
    void aplicarProducto(item.codigoArticulo);
  };

  const handleResolveExact = (codigo: string) => {
    const trimmed = codigo.trim();
    if (!debeResolverCodigoArticulo(trimmed)) return;
    if (
      codigoArticulo.trim().length > 0 &&
      trimmed.toUpperCase() === codigoArticulo.trim().toUpperCase()
    ) {
      return;
    }
    void aplicarProducto(trimmed);
  };

  return (
    <Stack spacing={0.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
        <Box sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}>
          <RecetaArticuloCodigoField
            label="Código"
            size="small"
            value={codigoInput}
            articuloResuelto={articuloResuelto}
            resolving={resolving}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error || ' '}
            modoPrecio="venta"
            sx={{ m: 0, width: '100%', '& input': { fontFamily: 'monospace' } }}
            onChange={(raw) => {
              setError(null);
              setCodigoInput(raw);
              if (!raw.trim() && codigoArticulo.trim()) {
                onChange({ codigoArticulo: '', descripcion: '' });
              }
            }}
            onSelect={handleSelect}
            onResolveExact={handleResolveExact}
            onOpenDialog={() => setDialogOpen(true)}
          />
        </Box>
        <TextField
          size="small"
          label="Descripción"
          value={descripcion}
          disabled
          fullWidth
          helperText=" "
          InputProps={{
            sx: {
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'text.primary',
                opacity: 0.87,
              },
            },
          }}
        />
      </Stack>

      <RecetaArticuloSearchDialog
        open={dialogOpen}
        title="Artículo de la nota de débito"
        modoPrecio="venta"
        initialSearchTerm={codigoInput}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
      />
    </Stack>
  );
};

export default ChequeTerceroRechazoArticuloField;
